/**
 * Videos Player Custom Element
 *
 * A custom HTML component that handles video playback with automatic
 * detection of popup-modal parents and video stopping when modals close.
 *
 * Usage:
 * <videos-player>
 *   <div class="video-container">
 *     <iframe src="https://www.youtube.com/embed/VIDEO_ID"></iframe>
 *   </div>
 * </videos-player>
 *
 * Features:
 * - Automatically detects if the component is within a popup-modal
 * - Stops all videos when the modal closes
 * - Supports iframes (YouTube, Vimeo, etc.) and HTML5 video elements
 * - Resets video content after stopping to allow proper re-initialization
 */
class VideosPlayer extends HTMLElement {
	constructor() {
		super();
		this.modalParent = null;
		this.videoElements = [];
		this.originalVideoContent = new Map();
		this.isInitialized = false;
	}

	connectedCallback() {
		if (this.isInitialized) return;
		this.isInitialized = true;

		this.modalParent = this.closest('popup-modal');
		this.initializeVideoElements();
		this.setupModalListeners();
		// Setup video thumbnail switching
		this.setupVideoThumbnails();
		
		// Setup external video player triggers
		this.setupVideoPlayerTriggers();
	}

	disconnectedCallback() {
		this.cleanup();
	}

	/**
	 * Initialize video elements and store original content
	 */
	initializeVideoElements() {
		// Find all video-related elements (video tags, iframes, etc.)
		this.videoElements = this.querySelectorAll(
			'video, iframe[src*="youtube"], iframe[src*="vimeo"], iframe[src*="player"], .video-container iframe, .video-tag iframe'
		);

		// Store original content for each video element
		this.videoElements.forEach((videoElement, index) => {
			this.originalVideoContent.set(index, {
				outerHTML: videoElement.outerHTML,
				innerHTML: videoElement.innerHTML,
				src: videoElement.src || videoElement.getAttribute('src'),
			});
		});
	}

	/**
	 * Setup modal event listeners
	 */
	setupModalListeners() {
		if (!this.modalParent) return;

		// Listen for modal close events
		this.modalParent.addEventListener('modal-close', this.handleModalClose.bind(this));

		// Listen for checkbox changes (modal open/close)
		const checkbox = document.getElementById(this.modalParent.dataset.toggle);
		if (checkbox) {
			this.checkboxObserver = new MutationObserver((mutations) => {
				mutations.forEach((mutation) => {
					if (mutation.type === 'attributes' && mutation.attributeName === 'checked') {
						if (!checkbox.checked) {
							this.stopAllVideos();
						}
					}
				});
			});
			this.checkboxObserver.observe(checkbox, { attributes: true });
		}

		// Listen for backdrop clicks and escape key
		const backdrop = this.modalParent.querySelector('[data-role="backdrop"]');
		if (backdrop) {
			backdrop.addEventListener('click', this.handleModalClose.bind(this));
		}

		// Listen for close button clicks
		const closeButtons = this.modalParent.querySelectorAll('label[for], button[data-toggle]');
		closeButtons.forEach((button) => {
			button.addEventListener('click', this.handleModalClose.bind(this));
		});
	}

	/**
	 * Setup video thumbnail switching functionality
	 */
	setupVideoThumbnails() {
		const thumbnailButtons = this.querySelectorAll('[data-video-tag]');
		const videoContainer = this.querySelector('.video-tag');

		if (!thumbnailButtons.length || !videoContainer) return;

		thumbnailButtons.forEach((button) => {
			button.addEventListener('click', (e) => {
				e.preventDefault();

				// If this button is already active, do nothing
				if (button.classList.contains('active')) {
					return;
				}

				const videoTag = button.dataset.videoTag;
				const videoTitle = button.dataset.videoTitle;

				if (videoTag) {
					// Update the video content
					videoContainer.innerHTML = videoTag;

					// Update the title if it exists
					const titleElement = this.querySelector('h4');
					if (titleElement && videoTitle) {
						titleElement.textContent = videoTitle;
					}

					// Update active state on thumbnails
					thumbnailButtons.forEach((btn) => btn.classList.remove('active'));
					button.classList.add('active');
				}
			});
		});
	}

	/**
	 * Setup external video player triggers
	 */
	setupVideoPlayerTriggers() {
		// Get the videos-player ID (either from id attribute or generate one)
		const videosPlayerId = this.id || `videos-player-${Math.random().toString(36).substr(2, 9)}`;
		if (!this.id) {
			this.id = videosPlayerId;
		}
		
		// Find elements with data-videos-player attribute that match this videos-player ID
		const triggers = document.querySelectorAll(`[data-videos-player="${videosPlayerId}"]`);
		
		triggers.forEach((trigger) => {
			trigger.addEventListener('click', (e) => {
				e.preventDefault();

                //if this is in a modal, open the modal
                if (this.modalParent && !this.modalParent.isOpen()) {
                    this.modalParent.open();
                }
				
				const videoIndex = trigger.dataset.video;
				if (videoIndex === undefined) return;
				
				// Find the corresponding thumbnail button with the same index
				const thumbnailButton = this.querySelector(`[data-video-index="${videoIndex}"]`);
				if (thumbnailButton) {
					// Trigger the thumbnail click programmatically
					thumbnailButton.click();
				}
			});
		});
	}

	/**
	 * Handle modal close events
	 */
	handleModalClose() {
		this.stopAllVideos();
	}

	/**
	 * Stop all videos by clearing and resetting iframe content
	 */
	stopAllVideos() {
		// Find current video elements dynamically (in case they were swapped via thumbnail clicks)
		const currentVideoElements = this.querySelectorAll(
			'video, iframe[src*="youtube"], iframe[src*="vimeo"], iframe[src*="player"], .video-container iframe, .video-tag iframe'
		);
		
		currentVideoElements.forEach((videoElement) => {
			this.stopVideo(videoElement);
		});
	}

	/**
	 * Stop a specific video element
	 */
	stopVideo(videoElement) {
		if (!videoElement) return;

        let originalSrc = null;
        

		try {
			// For iframes (YouTube, Vimeo, etc.)
			if (videoElement.tagName === 'IFRAME') {
                originalSrc = videoElement.src;
				// Clear the src to stop the video
				videoElement.src = '';
			}

			// For HTML5 video elements
			else if (videoElement.tagName === 'VIDEO') {
				videoElement.pause();
				videoElement.currentTime = 0;
			}

			// For video containers with iframes inside
			else if (
				videoElement.classList.contains('video-container') ||
				videoElement.classList.contains('video-tag')
			) {
				const iframe = videoElement.querySelector('iframe');
				if (iframe) {
					iframe.src = '';
				}
			}

            //finally, restore the original src
            if (originalSrc && videoElement.tagName === 'IFRAME') {
                videoElement.src = originalSrc;
            }
		} catch (error) {
			console.warn('Error stopping video:', error);
		}
	}

	/**
	 * Reset all videos to their original state
	 */
	resetAllVideos() {
		this.videoElements.forEach((videoElement, index) => {
			const originalContent = this.originalVideoContent.get(index);
			if (originalContent && videoElement.parentElement) {
				try {
					// For iframes, restore the src
					if (videoElement.tagName === 'IFRAME' && originalContent.src) {
						videoElement.src = originalContent.src;
					}
					// For other elements, restore the original HTML
					else if (originalContent.outerHTML) {
						const newElement = document.createElement('div');
						newElement.innerHTML = originalContent.outerHTML;
						const restoredElement = newElement.firstElementChild;
						if (restoredElement) {
							videoElement.parentElement.replaceChild(restoredElement, videoElement);
						}
					}
				} catch (error) {
					console.warn('Error resetting video:', error);
				}
			}
		});
	}

	/**
	 * Cleanup event listeners and observers
	 */
	cleanup() {
		if (this.checkboxObserver) {
			this.checkboxObserver.disconnect();
		}

		if (this.modalParent) {
			this.modalParent.removeEventListener('modal-close', this.handleModalClose.bind(this));
		}
	}

	/**
	 * Public method to manually stop all videos
	 */
	stopVideos() {
		this.stopAllVideos();
	}

	/**
	 * Public method to reset all videos
	 */
	resetVideos() {
		this.resetAllVideos();
	}
}

// Register the custom element
customElements.define('videos-player', VideosPlayer);

// Export for potential module usage
if (typeof module !== 'undefined' && module.exports) {
	module.exports = VideosPlayer;
}
